import {Component, HostBinding, HostListener, Inject, NgZone, OnInit, OnDestroy} from '@angular/core';
import {DOCUMENT} from "@angular/common";
import {animate, style, transition, trigger} from '@angular/animations';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';

import {OptionService} from './shared/services/option/option.service';
import {CordovaService} from './shared/services/cordova/cordova.service';
import {SystemService} from './shared/services/system/system.service';
import {UserService} from './shared/services/user/user.service';
import {GlobalEventService} from "./shared/services/global-event/global-event.service";
import {NativeVideoService} from "./shared/services/native-video/native-video.service";
import {BackBtnService} from "./shared/services/back-btn/back-btn.service";
import {SeoService} from './shared/services/seo/seo.service';
import {NaverLoginService} from './shared/services/naver-login/naver-login.service';
import {LayoutMgrService} from './shared/services/layout-mgr/layout-mgr.service';
// import $ from 'jquery';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({opacity: 0}),
        animate(300, style({opacity: 1}))
      ]),
      transition(':leave', [
        animate(300, style({opacity: 0}))
      ])
    ])
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private login$: Subscription;
  private deviceReady$: Subscription;
  private routeChange$: Subscription;
  private naverLoginError$?: Subscription;
  private pageNameSub$?: Subscription;
  private vvResizeCleanup?: () => void;

  @HostBinding('attr.class') get cssClass(): string {
    return 'seungjo' + (this.option.seungjoDispType + 1) +
      (this.systemSvc.ios ? ' ios' : '') +
      (this.systemSvc.notch ? ' notch' : '') +
      // (this.systemSvc.notch ? ' notch' : '') +
      (this.option.largeMode ? ' large-mode' : '');
  }

  public isSplash: boolean = true;
  /** 시각 숨김 H1에 바인딩되는 현재 페이지명. SeoService.currentPageName$ 구독으로 갱신. */
  public pageName = '';
  path: string;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private zone: NgZone,
    private systemSvc: SystemService,
    private cordovaSvc: CordovaService,
    private nativeVideoSvc: NativeVideoService,
    private option: OptionService,
    private userService: UserService,
    private globalEventSvc: GlobalEventService,
    private backBtnSvc: BackBtnService,
    private router: Router,
    private seoService: SeoService,
    private naverLoginSvc: NaverLoginService,
    private lm: LayoutMgrService,
  ) {
    this.isSplash = this.option.startAnim;

    this.login$ = this.userService.Login().subscribe();

    // 네이버 연동 검증 실패 통지: OptionsComponent 파괴 이후에도 사용자에게 안내한다.
    this.naverLoginError$ = this.naverLoginSvc.loginError.subscribe((error) => {
      console.error('AppComponent::constructor() naver loginError', error);
      this.lm.naverLoginPopup$?.next(null);
      this.lm.naverLoginPopup$?.complete();
      this.lm.OpenMsgPopupAlert(
        '네이버에서 받은 정보가 올바르지 않습니다.\n잠시 후 다시 시도해 주세요.',
        '네이버 연동 실패',
      ).subscribe({
        error: (alertError) => console.error('AppComponent::constructor() OpenMsgPopupAlert failed', alertError),
      });
    });

    this.deviceReady$ = this.cordovaSvc.deviceready.subscribe(() => {
      // 아이폰을 기본 모드를 세로로 고정합니다.
      (screen.orientation as any).lock('portrait');
      
      // iOS 디바이스에서 safe area 관련 CSS 변수를 재설정합니다.
      if (this.systemSvc.ios) {
        // 약간의 지연을 두고 CSS 변수를 다시 계산합니다.
        setTimeout(() => {
          const root = document.documentElement;
          const safeAreaBottom = getComputedStyle(root).getPropertyValue('--safe-area-inset-bottom');
          const safeAreaTop = getComputedStyle(root).getPropertyValue('--safe-area-inset-top');
          
          // 실제 env() 값 확인 후 CSS 변수를 다시 설정합니다.
          if (this.systemSvc.notch) {
            root.style.setProperty('--safe-area-inset-bottom', `env(safe-area-inset-bottom, 0px)`);
            root.style.setProperty('--safe-area-inset-top', `max(20px, env(safe-area-inset-top))`);
          } else {
            root.style.setProperty('--safe-area-inset-bottom', '0px');
            root.style.setProperty('--safe-area-inset-top', '20px');
          }
          
          console.log('Safe area CSS 변수가 재설정되었습니다');
        }, 300);

        // iOS 26 .container 과대 높이 보정 (#586): Cordova iOS의 모든 .container에 적용(검색 결과 목록 포함).
        // iOS 26에서 관측 — 100%가 safe-area를 이미 포함해 .container의 'height: 100% + safe-area' 계산이
        // 화면보다 커지고(노치 기기 실기기 진단: 945px = 852 + 59 + 34) 목록 하단이 고정 푸터 뒤로 깔림.
        // 실제 보이는 뷰포트 높이를 --app-vvh 로 노출하여 컨테이너 높이를 화면에 맞춘다.
        // 단, 키보드가 열리면 visualViewport.height가 줄어드는데 그대로 따라 줄이면 키보드에 가렸던 영역까지
        // 추가 스크롤되어 공백이 보인다. 그래서 '관측된 최대 높이'(키보드 없는 전체 화면)를 유지한다.
        // (키보드 없는 전체 높이는 세로 고정이라 안정적 → max로 복원 가능)
        // (--app-vvh는 Cordova iOS에서만 설정 → 그 외 환경은 기존 CSS 동작 유지: 웹·Android는 base height:100%, 구형 iOS는 calc 폴백)
        const vv = window.visualViewport;
        if (vv) {
          let maxVisualHeight = 0;
          const updateVisualHeight = () => {
            maxVisualHeight = Math.max(maxVisualHeight, vv.height);
            document.documentElement.style.setProperty('--app-vvh', `${Math.round(maxVisualHeight)}px`);
          };
          // CSS 변수만 갱신(Angular 상태와 무관)하므로 zone 밖에서 등록해 불필요한 변화 감지를 피한다.
          this.zone.runOutsideAngular(() => vv.addEventListener('resize', updateVisualHeight));
          this.vvResizeCleanup = () => vv.removeEventListener('resize', updateVisualHeight);
          updateVisualHeight();
        }
      }

      if (this.isSplash && 'plugins' in window) {

        const cordova = this.cordovaSvc.cordova;
        this.path = cordova.file.applicationDirectory + 'www/assets/video/intro_2.mp4';

        this.nativeVideoSvc.play(this.path).then(() => {
          console.log('video completed');
          this.isSplash = false;
        }, (err) => {
          console.log(err);
          this.isSplash = false;
        });

      }
    });
  }

  ngOnInit(): void {
    console.log('AppComponent::ngAfterViewInit()');

    this.pageNameSub$ = this.seoService.currentPageName$.subscribe(name => this.pageName = name);
    this.applySeoSafely(this.router.url);
    this.routeChange$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ).subscribe({
      next: (event) => this.applySeoSafely(event.urlAfterRedirects),
      error: (error) => console.error('AppComponent::routeChange$ stream error', error),
    });
  }

  ngOnDestroy(): void {
    // 각각의 구독을 해제합니다.
    if (this.login$) {
      this.login$.unsubscribe();
    }
    if (this.deviceReady$) {
      this.deviceReady$.unsubscribe();
    }
    if (this.routeChange$) {
      this.routeChange$.unsubscribe();
    }
    this.naverLoginError$?.unsubscribe();
    this.pageNameSub$?.unsubscribe();
    this.vvResizeCleanup?.();
  }

  @HostListener('click', ['$event'])
  onClick($event: MouseEvent) {
    // console.warn('AppComponent.onClick', $event);
    this.globalEventSvc.Click($event);
  }

  @HostListener('document:contextmenu', ['$event'])
  onRightClick(event: MouseEvent): void {
    event.preventDefault();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && (event.key === 'c' || event.key === 'C')) {
      event.preventDefault();
      alert('Copying is disabled.');
    }
  }

  private successCallback(): void {
    console.warn("Video was closed without error.");
    this.isSplash = false;
  }

  private applySeoSafely(rawPath: string): void {
    try {
      this.seoService.applyRouteSeo(rawPath);
    } catch (error) {
      console.error('AppComponent::applySeoSafely() failed', rawPath, error);
    }
  }
}
